let previousTransition: ViewTransition | null = null;
let previousFinished = true;

// A view transition that never finishes leaves its captured snapshot painted over the page for
// good: a flat, single-coloured screen with the live DOM alive underneath it, the previous screen's
// music still playing and its key handlers still bound. Elements with their own
// `view-transition-name` are captured separately, so parts of the chrome carry on rendering while
// the route content does not - which is what makes it look like a rendering bug rather than a stuck
// animation. Nothing rejects, so nothing reports it.
const TRANSITION_WATCHDOG_MS = 1_500;

export default function startViewTransition(callback: (isTransitioning: boolean) => void) {
  if (!document.startViewTransition) {
    callback(false);
  } else {
    try {
      // `finished` is a Promise, so the old `!previousTransition.finished` was always false and a
      // transition still in flight was never actually skipped.
      if (previousTransition && !previousFinished) {
        previousTransition.skipTransition();
      }

      previousFinished = false;
      previousTransition = document.startViewTransition(async () => {
        await callback(true);
      });

      const transition = previousTransition;
      const settle = () => {
        previousFinished = true;
        clearTimeout(watchdog);
      };
      // Both arms: `finished` rejects when a transition is skipped, and an unhandled rejection here
      // would be noise in every console for something already handled.
      transition.finished.then(settle, settle);

      const watchdog = setTimeout(() => {
        if (previousFinished) return;

        console.warn(`A view transition did not finish within ${TRANSITION_WATCHDOG_MS}ms - skipping it`);
        transition.skipTransition();
      }, TRANSITION_WATCHDOG_MS);

      return previousTransition;
    } catch (e) {
      console.error(e);
    }
  }
}
